import { ReusableModal } from '../modals/ReusableModal';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { LoadingIndicator } from '../helpers/LoadingIndicator';
import { useGetNoticeRecordQuery } from '../services/edgeApi';
import { Collapse } from 'bootstrap';
import { ChevronDownIcon, ChevronUpIcon } from '@iconicicons/react';
import { NoticeRecordHistory } from '../flows/NoticeRecordHistory';
import { getRawNoticeUrl } from './noticeSlice';

export type NoticeRecordModalProps = {
  noticeRecordId?: number | null;
  isOpen: boolean;
  onClose: () => void;
};

export const NoticeRecordModal: React.FC<NoticeRecordModalProps> = ({
  noticeRecordId,
  isOpen,
  onClose,
}) => {
  const metadataContainerRef = useRef<HTMLDivElement>(null);
  const [showMetadata, setShowMetadata] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [currentNoticeRecordId, setCurrentNoticeRecordId] =
    useState(noticeRecordId);
  useEffect(() => {
    setCurrentNoticeRecordId(noticeRecordId);
  }, [noticeRecordId]);    
  const { data: noticeRecord, isFetching } = useGetNoticeRecordQuery(
    {
      recordId: currentNoticeRecordId!,
    },
    { skip: !currentNoticeRecordId },
  );
  const metadataCollapse = useMemo(() => {
    if (!metadataContainerRef.current) return null;
    return new Collapse(metadataContainerRef.current, {
      toggle: false,
    });
  }, [metadataContainerRef.current]);

  const rawNoticeUrl = useMemo(
    () => (noticeRecord ? getRawNoticeUrl(noticeRecord) : ''),
    [noticeRecord],
  );

  useEffect(() => {
    if (!metadataCollapse) return;
    if (showMetadata) {
      metadataCollapse.show();
    } else {
      metadataCollapse.hide();
    }
  }, [showMetadata, metadataCollapse]);

  return (
    <>
      <ReusableModal size="lg" isOpen={isOpen} onClose={onClose}>
        <div>
          {isFetching ? (
            <div className="d-flex w-100 h-100 justify-content-center align-items-center my-4">
              <LoadingIndicator />
            </div>
          ) : noticeRecord ? (
            <div>
              <div className="d-flex justify-content-between align-items-center">
                <h4>
                  {noticeRecord.tsp_name} Notice {noticeRecord.notice_id}
                </h4>
                {currentNoticeRecordId !== noticeRecordId && (
                  <button
                    className="btn btn-link p-0"
                    onClick={() => setCurrentNoticeRecordId(noticeRecordId)}
                  >
                    Return to Original
                  </button>
                )}
                <span>Parse: {noticeRecord.display_details?.parse_state}</span>
              </div>
              <div>
                <span className="text-muted fs-sm">
                  Notice Record ID #{noticeRecord.id}
                </span>
              </div>
              <div>
                <table className="table table-bordered">
                  <tbody>
                    {Boolean(noticeRecord.display_details?.subject) && (
                      <tr>
                        <th>Subject</th>
                        <td colSpan={3}>
                          {noticeRecord.display_details?.subject}
                        </td>
                      </tr>
                    )}
                    {Boolean(noticeRecord.display_details?.posted) && (
                      <tr>
                        <th>Posted</th>
                        <td>{noticeRecord.display_details?.posted}</td>
                        <td
                          colSpan={2}
                          className="text-end"
                          style={{ width: '40%' }}
                        >
                          {noticeRecord.display_details?.notice_type}
                        </td>
                      </tr>
                    )}
                    <tr>
                      <th style={{ width: '20%' }}>Location</th>
                      <td colSpan={2}>
                        {noticeRecord.identifier}
                        {Boolean(noticeRecord.display_values?.direction) && (
                          <span>
                            &nbsp;({noticeRecord.display_values?.direction})
                          </span>
                        )}
                      </td>
                      <td
                        colSpan={2}
                        className="text-end"
                        style={{ width: '20%' }}
                      >
                        <button
                          className="btn btn-link p-0"
                          onClick={() => setShowHistory(true)}
                        >
                          Location&nbsp;History
                        </button>
                      </td>
                    </tr>
                    <tr>
                      <th>Date&nbsp;Range</th>
                      <td colSpan={3}>{noticeRecord.display_daterange}</td>
                    </tr>
                    <tr>
                      <th>Effect</th>
                      <td colSpan={3}>
                        {noticeRecord.display_values?.effect_text}
                      </td>
                    </tr>
                    <tr>
                      <th>Primary&nbsp;Source</th>
                      <td colSpan={3}>"{noticeRecord.reason}"</td>
                    </tr>
                    {(noticeRecord.prior_notice_id != null ||
                      noticeRecord.resolved_prior_notice_id != null ||
                      noticeRecord.resolved_supersede_notice_id != null) && (
                      <tr>
                        <th style={{ width: '20%' }}>Prior&nbsp;Notice</th>
                        <td style={{ width: '40%' }}>
                          {noticeRecord.display_details?.prior_record_id ? (
                            <button
                              className="btn btn-link p-0"
                              onClick={() =>
                                setCurrentNoticeRecordId(
                                  noticeRecord.display_details?.prior_record_id!,
                                )
                              }
                            >
                              {noticeRecord.prior_notice_id ??
                                noticeRecord.resolved_prior_notice_id}
                            </button>
                          ) : (
                            noticeRecord.prior_notice_id ??
                            noticeRecord.resolved_prior_notice_id
                          )}
                        </td>
                        <th style={{ width: '20%' }}>Superseded&nbsp;By</th>
                        <td style={{ width: '20%' }}>
                          {noticeRecord.display_details?.supersede_record_id ? (
                            <button
                              className="btn btn-link p-0"
                              onClick={() =>
                                setCurrentNoticeRecordId(
                                  noticeRecord.display_details?.supersede_record_id!,
                                )
                              }
                            >
                              {noticeRecord.resolved_supersede_notice_id}
                            </button>
                          ) : (
                            noticeRecord.resolved_supersede_notice_id
                          )}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              <div className="d-flex justify-content-between align-items-center">
                <h5>Scraped Notice</h5>
                <span
                  className="d-flex flex-column"
                  style={{ fontSize: '0.75rem' }}
                >
                  <i>Scraped images and links may no longer resolve.</i>
                  {process.env.NODE_ENV === 'development' && (
                    <span>
                      <span className="text-warning fw-bold me-2">
                        <i>DEV</i>
                      </span>
                      <i>Notice access requires local Django login.</i>
                    </span>
                  )}
                </span>
                <a className="btn btn-link" href={rawNoticeUrl} target="_blank">
                  Open In New Window
                </a>
              </div>
              <div>
                <iframe
                  src={rawNoticeUrl}
                  title="Scraped Notice"
                  style={{
                    width: '100%',
                    height: '50vh',
                    border: '1px solid #ccc',
                  }}
                />
              </div>
              <div>
                <hr />
                <p
                  className="text-muted my-2"
                  role="button"
                  onClick={() => setShowMetadata((p) => !p)}
                >
                  {showMetadata ? 'Hide' : 'Show'} Metadata{' '}
                  {showMetadata ? <ChevronUpIcon /> : <ChevronDownIcon />}
                </p>
                <div className="collapse" ref={metadataContainerRef}>
                  <pre className="bg-light p-2 rounded">
                    {JSON.stringify(noticeRecord.metadata, null, 2)}
                  </pre>
                </div>
              </div>
            </div>
          ) : (
            <p>No additional details available.</p>
          )}
        </div>
      </ReusableModal>
      {showHistory && (
        <NoticeRecordHistory
          componentId={noticeRecord?.notice_component ?? ''}
          setHistoryModal={() => setShowHistory(false)}
          getFlowsOnDate={() => {}}
        />
      )}
    </>
  );
};
